import { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";
import { IGetPayrollData, IGetPayrollDataRow } from "@/Washouts/types/PayrollClosing/queries";
import { getPayrollPreview } from "@/Washouts/api/PayrollClosing/queries";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { nullishResolver } from "@/Common/helpers/sonar";
import { styled } from "@mui/material/styles";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { PayrollPreviewTable } from "../../tables/PayrollClosing/PayrollPreviewTable";
import { enqueueSnackbar, SnackbarKey } from "notistack";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
import { ICombinedPositionStatuses } from "@/Washouts/types/PayrollClosing/payrollSubmission";
import { ActionRefreshOnDemand } from "@/Layouts/components/ActionRefreshOnDemand";
import { IPayPeriodProviders } from "@/Common/api/types/CommissionCycle";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { requestUpdatePeriodPayrollProviders } from "@/Washouts/api/PayPeriodsProviders/mutations";
import { LoadingButton } from "@mui/lab";
import { ISubmitPayrollToHRArgs } from "@/Washouts/types/PayrollClosing/mutations";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { submitPayrollToHR } from "@/Washouts/api/PayrollClosing/mutations";

const COMPANY_ID_LABEL = "Company ID";
const EMPTY_PAY_PERIOD_PROVIDERS: IPayPeriodProviders[] = [];
const EMPTY_COMBINED_POSITION_STATUSES: ICombinedPositionStatuses[] = [];

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

const renderSnackbarsActions = (key: SnackbarKey) => {
	return <SnackbarsActions key={key.toString()} index={key} />;
};

const hasPostedPayrollPosition = (data: ICombinedPositionStatuses[], positionsMap: Record<string, string[]>) => {
	return Object.entries(positionsMap).some(([cycleId, positions]) =>
		data.some((item) => item.commissionCycleId === cycleId && item.status === "POSTED_PAYROLL" && positions.includes(item.position))
	);
};

const getAvailableProviders = (payPeriodsProviders: IPayPeriodProviders[]) => {
	return payPeriodsProviders.filter((entry) => entry.status !== "COMPLETED") ?? null;
};

type TCommissionCyclePayrollDialogProps = {
	locationId: string;
	commissionCycleId?: string | null;
	amountOfSelectedPositions?: number;
	selectedPositions: Record<string, string[]>;
	payPeriodId?: string | null;
	combinedPositionStatuses: ICombinedPositionStatuses[];
	payPeriodsProviders?: IPayPeriodProviders[];
	clearSelectedPositions: () => void;
};

const CommissionCyclePayrollDialog = (props: TCommissionCyclePayrollDialogProps) => {
	const {
		locationId = "",
		commissionCycleId = "",
		payPeriodId = "",
		selectedPositions,
		clearSelectedPositions,
		amountOfSelectedPositions = 0,
		combinedPositionStatuses: combinedPositionStatusesProp,
		payPeriodsProviders: payPeriodsProvidersProp,
	} = props;
	const combinedPositionStatuses = combinedPositionStatusesProp ?? EMPTY_COMBINED_POSITION_STATUSES;
	const payPeriodsProviders = payPeriodsProvidersProp ?? EMPTY_PAY_PERIOD_PROVIDERS;
	const [openDialog, setOpenDialog] = useState(false);
	const [storedPreviewData, setStoredPreviewData] = useState<IGetPayrollData[] | null>([]);
	const [previewData, setPreviewData] = useState<IGetPayrollData | null>(null);
	const [selectedProviders, setSelectedProviders] = useState<IPayPeriodProviders[] | null>(
		getAvailableProviders(payPeriodsProviders) ?? null
	);
	const [submitDataDivided, setSubmitDataDivided] = useState<Record<string, IGetPayrollData[]> | null>(null);
	const user = useSelector((state: RootState) => state.user.user);
	const isSomethingSelected = amountOfSelectedPositions > 0;
	const isDisabledPreview = !isSomethingSelected;
	const arraySelectedPositions = Object.entries(selectedPositions);

	const handleError = (message: string) => () => {
		setOpenDialog(false);
		enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
			variant: "error",
			preventDuplicate: true,
			action: renderSnackbarsActions,
		});
	};

	const extractCompanyIdFromRow = (data: IGetPayrollDataRow) => {
		const companyId = data.row.find((row) => row.label === COMPANY_ID_LABEL);
		return companyId?.value;
	};

	const filterByCompanyId = (data: IGetPayrollDataRow[]) => {
		return data.filter((data) => {
			const companyId = extractCompanyIdFromRow(data);
			return selectedProviders?.some((provider) => provider.companyId === companyId);
		});
	};

	const updatePreviewData = (result: IGetPayrollData[] | null = []) => {
		if (result?.length) {
			const columns = result[0].columns;
			let data: IGetPayrollDataRow[] = [];
			for (const entry of result) {
				data = data.concat(filterByCompanyId(entry.data));
			}
			setPreviewData({
				columns,
				data,
			} as IGetPayrollData);
		}
	};

	const { mutate: getPayroll, isPending: isGettingPreview } = useMutation({
		mutationFn: async () => {
			const wrappedPromises: Array<
				Promise<{
					commissionCycleId: string;
					result: IGetPayrollData[] | null | undefined;
				}>
			> = [];

			for (const element of arraySelectedPositions) {
				const [commissionCycleId, positions] = element;
				if (positions.length) {
					const promise = getPayrollPreview(locationId, commissionCycleId, payPeriodId ?? "", positions).then((result) => ({
						commissionCycleId,
						result,
					}));
					wrappedPromises.push(promise);
				}
			}

			const queryResult = await Promise.all(wrappedPromises);

			const dataResults = queryResult.reduce(
				(acc, { commissionCycleId, result }) => {
					if (!result || !result.length) {
						return acc;
					}
					if (!acc[commissionCycleId]) {
						acc[commissionCycleId] = [];
					}
					acc[commissionCycleId].push(...result);
					return acc;
				},
				{} as Record<string, IGetPayrollData[]>
			);
			setSubmitDataDivided(dataResults);
			return [...nullishResolver(queryResult?.[1]?.result, []), ...nullishResolver(queryResult?.[0]?.result, [])];
		},
		onSuccess: (result) => {
			setStoredPreviewData(result);
			updatePreviewData(result);
		},
		onError: () => {
			handleError("An error occurred previewing the payroll.");
		},
	});

	const { mutate: requestUpdatePayrollProviders, isPending: isRefreshingProviders } = useMutation({
		mutationFn: async () => {
			if (!payPeriodId) {
				return false;
			}
			const payPeriodsIds = [payPeriodId];
			return requestUpdatePeriodPayrollProviders({ locationId, payPeriodsIds });
		},
	});

	const { mutate: submitPayrollMutate, isPending: isSubmittingPayroll } = useMutation({
		mutationKey: ["submitPayrollToHR", locationId, commissionCycleId, payPeriodId],
		mutationFn: (params: ISubmitPayrollToHRArgs) => submitPayrollToHR(params),
		onSuccess: () => {
			clearSelectedPositions();
			setOpenDialog(false);
		},
		onError: () => {
			//TODO: check error from HR submission, it could be a partial error
			handleError("An error occurred submitting the payroll.");
			clearSelectedPositions();
			setOpenDialog(false);
		},
	});

	const handleDialogOpen = () => {
		setOpenDialog(true);
		if (!isSomethingSelected) {
			return;
		}
		getPayroll();
	};

	const handleSelectProvider = (selectedPayPeriodProviders: IPayPeriodProviders[]) => {
		setSelectedProviders(selectedPayPeriodProviders);
	};

	const onCloseHandler = () => {
		setOpenDialog(false);
	};

	const handleSubmitPayroll = async () => {
		const companyIdProviderPayPeriodIdPairs =
			selectedProviders?.map((provider) => ({
				companyId: provider.companyId,
				providerPayPeriodId: provider.providerPayPeriodId,
			})) ?? [];

		for (const commissionCycleIdKey of Object.keys(submitDataDivided ?? {})) {
			const dataEntries = submitDataDivided?.[commissionCycleIdKey] ?? [];
			submitPayrollMutate({
				locationId,
				payPeriodId,
				companyIdProviderPayPeriodIdPairs,
				commissionCycleId: commissionCycleIdKey,
				submitterId: user?._id ?? "",
				payrollData: dataEntries,
			} as ISubmitPayrollToHRArgs);
		}
	};

	useEffect(() => {
		setSelectedProviders(getAvailableProviders(payPeriodsProviders) ?? null);
		return () => {};
	}, [payPeriodsProviders]);

	useEffect(() => {
		updatePreviewData(storedPreviewData);
		return () => {};
	}, [selectedProviders]);

	return (
		<>
			<Stack>
				<Button
					variant="contained"
					className={clsx({
						"h-[30px] !rounded-md !text-sm !text-nowrap !text-white !capitalize !font-sans": true,
						"!bg-primary hover:!shadow-primary-shadow-50": !isDisabledPreview,
						"!bg-disabled hover:cursor-default": isDisabledPreview,
					})}
					disabled={isDisabledPreview}
					onClick={handleDialogOpen}
					disableElevation
				>
					Preview {isSomethingSelected ? amountOfSelectedPositions : ""} Submission
				</Button>
			</Stack>
			<BootstrapDialog open={openDialog} onClose={onCloseHandler} maxWidth={false}>
				<DialogTitle>
					{isGettingPreview ? (
						<Grid container direction="row" justifyContent="space-between" alignItems="center">
							<Grid item>
								<Skeleton variant="rectangular" width={220} height={30} className="rounded-md" />
							</Grid>
							<Grid item>
								<Skeleton variant="circular" width={30} height={30} />
							</Grid>
						</Grid>
					) : (
						<>
							<Typography className="payroll-preview-dialog-title" component="div">
								Payroll Preview
							</Typography>
							<IconButton aria-label="close" size="medium" className="payroll-preview-close-btn" onClick={onCloseHandler}>
								<CloseRoundedIcon fontSize="inherit" />
							</IconButton>
						</>
					)}
				</DialogTitle>
				<DialogContent className="w-[1200px]">
					{isGettingPreview ? (
						<TableLoader
							sx={{
								marginTop: "-10px !important",
							}}
							content
							rows={3}
						/>
					) : (
						<PayrollPreviewTable data={previewData} />
					)}
				</DialogContent>
				<DialogActions className="!px-[20px] !my-3">
					{isGettingPreview ? (
						<Stack direction={"row"} alignItems={"center"} justifyContent="flex-end" gap={2} className="!w-full">
							<Stack direction={"row"} alignItems={"center"} gap={2} sx={{ margin: "0 10px 10px 0" }}>
								<Skeleton variant="rectangular" width={200} height={30} className="rounded-md" />
								<Skeleton variant="rectangular" width={110} height={30} className="rounded-md" />
							</Stack>
						</Stack>
					) : (
						<Stack direction={"row"} alignItems={"center"} justifyContent="space-between" gap={2} className="!w-full">
							<Stack>
								<Stack direction={"row"} alignItems={"flex-start"} className="!text-warning max-w-[400px]">
									{hasPostedPayrollPosition(combinedPositionStatuses, selectedPositions) && (
										<>
											<WarningAmberRoundedIcon
												sx={{ width: "15px", height: "15px", marginTop: "2px", marginRight: "6px" }}
											/>
											<Typography className="!text-sm !font-sans !text-warning">
												This preview contains positions that have already been submitted this pay period. If
												submitted, this preview will replace the existing submission.
											</Typography>
										</>
									)}
								</Stack>
							</Stack>
							<Stack direction={"row"} alignItems={"center"} gap={2}>
								<Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
									<Stack>
										<ActionRefreshOnDemand
											tooltip={"Load New Pay Period IDs"}
											loading={isRefreshingProviders}
											loadingProps={{ className: "items-center" }}
											onClick={requestUpdatePayrollProviders}
										/>
									</Stack>
									<Stack>
										<Typography className="!font-sans !text-[14.5px] text-default">Pay Period ID:</Typography>
									</Stack>
									<Stack>
										<SmartSelector
											classes="!w-[253px]"
											multiple
											tagsVariant={"text"}
											showBy={"providerPayPeriodId"}
											options={payPeriodsProviders}
											selected={selectedProviders}
											groupingProps={{
												groupLabel(value) {
													return `Display ID: ${value}`;
												},
												groupBy: "displayId",
												sticky: true,
												classes: "!text-primary !font-bold !font-montserrat",
											}}
											inputProps={{
												placeholder: "Select Pay Period ID",
												hideTooltip: true,
											}}
											listBoxProps={{
												sx: { maxHeight: "280px" },
											}}
											popperProps={{ className: "!z-[1350]" } as object}
											disabled={isRefreshingProviders}
											hideTooltip={true}
											onChangeAction={(values) => handleSelectProvider(values as IPayPeriodProviders[])}
										/>
									</Stack>
								</Stack>
								<LoadingButton
									loading={isSubmittingPayroll}
									variant="contained"
									className={clsx({
										"!rounded-md !h-[30px] !w-[110px] !text-sm !text-nowrap !text-white !capitalize !font-sans": true,
										"!bg-primary hover:!shadow-primary-shadow-50": selectedProviders?.length && !isSubmittingPayroll,
										"!bg-disabled": isSubmittingPayroll || !selectedProviders?.length,
									})}
									disabled={isSubmittingPayroll || !selectedProviders?.length}
									onClick={handleSubmitPayroll}
									disableElevation
								>
									{isSubmittingPayroll ? null : "Submit Payroll"}
								</LoadingButton>
							</Stack>
						</Stack>
					)}
				</DialogActions>
			</BootstrapDialog>
		</>
	);
};

export default CommissionCyclePayrollDialog;
